import React from "react";
import { useAppSelector } from "../redux";
import { Card, PageHeader, StatusBadge } from "../components/common";
import { getUserDisplayName } from "../utils";
import "./Profile.css";

interface DetailField {
  label: string;
  value?: string | number | null;
}

interface DetailSectionProps {
  title: string;
  fields: DetailField[];
}

const DetailSection: React.FC<DetailSectionProps> = ({ title, fields }) => {
  const visibleFields = fields.filter((field) => field.value !== undefined && field.value !== null && field.value !== "");

  if (visibleFields.length === 0) {
    return null;
  }

  return (
    <div className="profile-detail-section">
      <h4 className="profile-detail-section-title">{title}</h4>
      <div className="profile-detail-grid">
        {visibleFields.map((field) => (
          <div className="profile-detail-item" key={field.label}>
            <span className="profile-detail-label">{field.label}</span>
            <span className="profile-detail-value">{field.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Profile: React.FC = () => {
  const { user, role } = useAppSelector((state) => state.auth);

  if (!user) {
    return null;
  }

  const displayName = getUserDisplayName(user);
  const teacherName = user.Allocated_Teacher?.name || user.Teacher?.name || user.Teachers_Name;
  const parentName = [user.Parent_First_Name, user.Parent_Last_Name].filter(Boolean).join(" ");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card style={{ padding: "28px" }}>
        <PageHeader title="My Profile" subtitle="View your personal and academic information." />

        <div className="profile-header">
          <div className="profile-avatar">
            {displayName ? displayName.charAt(0).toUpperCase() : "?"}
          </div>
          <div className="profile-header-text">
            <h2 className="profile-name">{displayName || "—"}</h2>
            <div className="profile-header-badges">
              {user.Student_Number && <StatusBadge variant="primary">{user.Student_Number}</StatusBadge>}
              {role && (
                <StatusBadge variant="secondary">
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </StatusBadge>
              )}
            </div>
          </div>
        </div>

        <div className="profile-sections">
          <DetailSection
            title="Contact Information"
            fields={[
              { label: "Email Address", value: user.Email },
              { label: "Phone", value: user.Phone },
            ]}
          />

          <DetailSection
            title="Personal Information"
            fields={[
              { label: "Gender", value: user.Gender },
              { label: "Month of Birth", value: user.Month_of_Birth },
              { label: "Year of Birth", value: user.Year_of_Birth },
            ]}
          />

          <DetailSection
            title="Academic Information"
            fields={[
              { label: "Centre", value: user.Centre },
              { label: "Mandal", value: user.Mandal },
              { label: "Level", value: user.What_level_are_you_in || user.Level },
              { label: "Commitment Level", value: user.Commitment_Level },
              { label: "Allocated Teacher", value: teacherName },
            ]}
          />

          <DetailSection
            title="Parent / Guardian Information"
            fields={[
              { label: "Parent Name", value: parentName },
              { label: "Parent Email", value: user.Parent_Email },
              { label: "Parent Mobile", value: user.Parent_Mobile },
            ]}
          />
        </div>
      </Card>
    </div>
  );
};

export default Profile;
